import { useEffect, useRef, useState } from 'react';
import { usePromotionStore } from '~contexts/mobx';

/**
 * Coupon-selection data source (reusable).
 *
 * Fetches the coupons (`GET /analytics/coupons/options`, via
 * `PromotionStore.fetchCoupons`) and exposes them as `{ id, label }` options with
 * explicit loading / ready / empty / error states. Slice 4 uses it to auto-pick a
 * default coupon for the Overview "Coupon activated" card; slice 5 reuses the same
 * hook to back a visible dropdown, so the fetch/mapping lives here once, not in
 * either card.
 */

/** One selectable coupon: `id` is the promotion id (stringified for use as a
 * query param and a `<select>` option value), `label` is its localized title. */
export interface CouponOption {
    id: string;
    label: string;
    /** False for a public coupon, redeemed without a grant. */
    isPrivate: boolean;
    /** True for a reusable coupon, scanned in store and never activated in the app. */
    isUnlimitedRedemption: boolean;
}

/** `idle` = selection disabled (never fetched). The rest mirror the usual
 * async lifecycle; `empty` is a successful fetch with no coupons. */
export type CouponListState =
    | { status: 'idle' }
    | { status: 'loading' }
    | { status: 'ready'; coupons: CouponOption[] }
    | { status: 'empty' }
    | { status: 'error' };

/** Minimal shape the mapper needs from a promotion row. Kept structural (not the
 * full `Promotion` type) so it is trivial to construct in tests and so the store
 * method's `Promotion[]` result is assignable without a cast. */
interface CouponSourceItem {
    title?: string | null;
    language?: string | null;
}
interface CouponSourceRow {
    id: number;
    isPrivate?: boolean | null;
    isUnlimitedRedemption?: boolean | null;
    items?: CouponSourceItem[] | null;
}

/**
 * Maps promotion rows to coupon options. The label prefers the English item's
 * title, falls back to the first item's title, and finally to `Coupon <id>` when
 * no usable (non-blank) title is present, so a coupon always renders a label.
 *
 * A title two coupons share gets ` (#<id>)` on both, so the dropdown can tell
 * them apart.
 *
 * The result is deterministically ordered (label alphabetical, case-insensitive,
 * tie-broken by numeric id) so the auto-picked default (`coupons[0]`) and the
 * dropdown order are stable across loads regardless of the backend's response
 * order. `fetchCoupons` sends no sort, so mapping is the one place the order is pinned.
 */
export const mapPromotionsToCoupons = (
    rows: CouponSourceRow[]
): CouponOption[] => {
    const titled = rows.map((row) => {
        const items = row.items ?? [];
        const localized = items.find((i) => i.language === 'en') ?? items[0];
        const title = localized?.title?.trim();
        return {
            id: String(row.id),
            numericId: row.id,
            label: title ? title : `Coupon ${row.id}`,
            isPrivate: row.isPrivate !== false,
            isUnlimitedRedemption: row.isUnlimitedRedemption === true,
        };
    });
    const labelCounts = new Map<string, number>();
    for (const { label } of titled) {
        labelCounts.set(label, (labelCounts.get(label) ?? 0) + 1);
    }
    return titled
        .sort(
            (a, b) =>
                a.label.localeCompare(b.label, undefined, {
                    sensitivity: 'base',
                }) || a.numericId - b.numericId
        )
        .map(({ id, label, isPrivate, isUnlimitedRedemption }) => ({
            id,
            label:
                (labelCounts.get(label) ?? 0) > 1 ? `${label} (#${id})` : label,
            isPrivate,
            isUnlimitedRedemption,
        }));
};

/**
 * Fetches the coupon list once when `enabled`. Returns `idle` (no fetch) while
 * disabled, so a card can call the hook unconditionally (hook rules) and only pay
 * for the request when its coupon selection is actually on. A list once `ready`
 * is reused when `enabled` turns on again; any other outcome is fetched again.
 */
export const useCouponList = (enabled = true): CouponListState => {
    const promotionStore = usePromotionStore();
    const [state, setState] = useState<CouponListState>(
        enabled ? { status: 'loading' } : { status: 'idle' }
    );
    const readyRef = useRef<CouponListState>();

    useEffect(() => {
        if (!enabled) {
            setState({ status: 'idle' });
            return;
        }
        if (readyRef.current) {
            setState(readyRef.current);
            return;
        }
        let cancelled = false;
        setState({ status: 'loading' });
        promotionStore
            .fetchCoupons()
            .then((rows) => {
                if (cancelled) return;
                if (rows == null) {
                    setState({ status: 'error' });
                    return;
                }
                const coupons = mapPromotionsToCoupons(rows);
                if (coupons.length === 0) {
                    setState({ status: 'empty' });
                    return;
                }
                readyRef.current = { status: 'ready', coupons };
                setState(readyRef.current);
            })
            .catch(() => {
                if (!cancelled) setState({ status: 'error' });
            });
        return () => {
            cancelled = true;
        };
    }, [promotionStore, enabled]);

    return state;
};
